import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  ShopProfileUpdate,
  GenerateReportRequest,
  RefineReportRequest,
  FinalizeReportRequest,
} from '@/types';

// Query keys
export const queryKeys = {
  entitlement: ['entitlement'] as const,
  sessions: (params?: Record<string, unknown>) => ['sessions', params] as const,
  session: (key: string) => ['session', key] as const,
  vehicles: ['vehicles'] as const,
  vehicle: (vin: string) => ['vehicle', vin] as const,
  reports: (vin?: string) => ['reports', vin] as const,
  shopProfile: ['shopProfile'] as const,
  shopLogo: ['shopLogo'] as const,
};

// Entitlement
export function useEntitlement() {
  return useQuery({
    queryKey: queryKeys.entitlement,
    queryFn: () => apiClient.getEntitlement(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

// Sessions
export function useSessions(params?: {
  page?: number;
  pageSize?: number;
  vin?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    queryKey: queryKeys.sessions(params),
    queryFn: () => apiClient.getSessions(params),
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useSession(key: string) {
  return useQuery({
    queryKey: queryKeys.session(key),
    queryFn: () => apiClient.getSession(key),
    enabled: !!key,
    staleTime: 60 * 1000,
  });
}

// Vehicles
export function useVehicles() {
  return useQuery({
    queryKey: queryKeys.vehicles,
    queryFn: () => apiClient.getVehicles(),
    staleTime: 60 * 1000,
  });
}

export function useVehicle(vin: string) {
  return useQuery({
    queryKey: queryKeys.vehicle(vin),
    queryFn: () => apiClient.getVehicle(vin),
    enabled: !!vin,
    staleTime: 60 * 1000,
  });
}

// Reports
export function useReports(vin?: string) {
  return useQuery({
    queryKey: queryKeys.reports(vin),
    queryFn: () => apiClient.getReports(vin),
    staleTime: 60 * 1000,
  });
}

export function useReportDownload() {
  return useMutation({
    mutationFn: (reportId: string) => apiClient.getReportDownload(reportId),
    onSuccess: (data) => {
      // Open download URL in new tab
      window.open(data.downloadUrl, '_blank');
    },
  });
}

// Shop Profile
export function useShopProfile() {
  return useQuery({
    queryKey: queryKeys.shopProfile,
    queryFn: () => apiClient.getShopProfile(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateShopProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profile: ShopProfileUpdate) => apiClient.updateShopProfile(profile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shopProfile });
      queryClient.invalidateQueries({ queryKey: queryKeys.shopLogo });
    },
  });
}

export function useShopLogoUrl() {
  return useQuery({
    queryKey: queryKeys.shopLogo,
    queryFn: () => apiClient.getShopLogoUrl(),
    staleTime: 60 * 60 * 1000, // 1 hour (logo URLs are long-lived)
  });
}

// Report SAS URL (for viewer page — no side-effect)
export function useReportSasUrl(reportId: string | undefined) {
  return useQuery({
    queryKey: ['reportSasUrl', reportId],
    queryFn: () => apiClient.getReportDownload(reportId!),
    enabled: !!reportId,
    staleTime: 10 * 60 * 1000, // 10 min (SAS URLs typically last 30-60 min)
  });
}

// Vehicle AI Summary
export function useVehicleSummary(vin: string | undefined) {
  return useQuery({
    queryKey: ['vehicle-summary', vin],
    queryFn: () => apiClient.getVehicleSummary(vin!),
    enabled: !!vin && vin !== 'NO VIN' && vin.length >= 10,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

// Report Generation
export function useGenerateReport() {
  return useMutation({
    mutationFn: (body: GenerateReportRequest) => apiClient.generateReport(body),
  });
}

// Report Refinement
export function useRefineReport() {
  return useMutation({
    mutationFn: (body: RefineReportRequest) => apiClient.refineReport(body),
  });
}

// Report Finalization
export function useFinalizeReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: FinalizeReportRequest) => apiClient.finalizeReport(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

// Rich Session Data (CommLogProcessor APIs)
export function useCommLogJson(vin: string | null | undefined, sessionKey?: string | null) {
  return useQuery({
    queryKey: ['commLogJson', vin, sessionKey ?? null] as const,
    queryFn: () => apiClient.getCommLogJson(vin!, sessionKey ?? undefined),
    // Keyed on the session, not the VIN: a session recorded without a VIN still has all its data,
    // and gating on the VIN made those sessions say "No detailed telemetry captured".
    enabled: !!sessionKey,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function useSecondBySecondData(sessionKey: string | null | undefined) {
  return useQuery({
    queryKey: ['secondBySecond', sessionKey] as const,
    queryFn: () => apiClient.getSecondBySecondData(sessionKey!),
    enabled: !!sessionKey,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

// NHTSA Free VIN Decode (auto-called, cached by VIN)
export function useNhtsaVinDecode(vin: string | null | undefined) {
  return useQuery({
    queryKey: ['nhtsaVinDecode', vin] as const,
    queryFn: () => apiClient.decodeVinNhtsa(vin!),
    enabled: !!vin && vin.length === 17,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (VIN decode data is static)
    gcTime: 7 * 24 * 60 * 60 * 1000, // Keep in cache for 7 days
    retry: 1, // Only retry once on failure
  });
}

// VehicleDatabases API hooks (Premium - paid, click-to-load pattern)
// All require explicit `enabled` flag to prevent auto-loading on page load

export function useVinDecode(vin: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['vinDecode', vin] as const,
    queryFn: () => apiClient.decodeVin(vin!),
    enabled: enabled && !!vin,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (VIN decode data is static)
  });
}

export function useMarketValue(vin: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['marketValue', vin] as const,
    queryFn: () => apiClient.getMarketValue(vin!),
    enabled: enabled && !!vin,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useTitleCheck(vin: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['titleCheck', vin] as const,
    queryFn: () => apiClient.getTitleCheck(vin!),
    enabled: enabled && !!vin,
    staleTime: 24 * 60 * 60 * 1000,
  });
}

export function useAuction(vin: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['auction', vin] as const,
    queryFn: () => apiClient.getAuction(vin!),
    enabled: enabled && !!vin,
    staleTime: 60 * 60 * 1000,
  });
}

export function useWarranty(
  year: string | null | undefined,
  make: string | null | undefined,
  model: string | null | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['warranty', year, make, model] as const,
    queryFn: () => apiClient.getWarranty(year!, make!, model!),
    enabled: enabled && !!year && !!make && !!model,
    staleTime: 24 * 60 * 60 * 1000,
  });
}

export function useMaintenance(vin: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['maintenance', vin] as const,
    queryFn: () => apiClient.getMaintenance(vin!),
    enabled: enabled && !!vin,
    staleTime: 24 * 60 * 60 * 1000,
  });
}

export function useCommonRepairs(vin: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['commonRepairs', vin] as const,
    queryFn: () => apiClient.getCommonRepairs(vin!),
    enabled: enabled && !!vin,
    staleTime: 24 * 60 * 60 * 1000,
  });
}

export function useSalesHistory(vin: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['salesHistory', vin] as const,
    queryFn: () => apiClient.getSalesHistory(vin!),
    enabled: enabled && !!vin,
    staleTime: 60 * 60 * 1000,
  });
}
